import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  APIcreateTerm,
  APIupdateTerm,
} from '../../../api/Metadata';

import { fetchTaxonomies } from '../../../actions/Metadata';

import { addFlashMessage } from '../../../helpers/FlashMessages';

import ProcessingSpinner from '../../ProcessingSpinner';

import ErrorBoundary from '../../ErrorBoundary';

class TermEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      name: null,
      blocked: false,
      errors: [],
      saving: false,
    };

    this.inputRef = React.createRef();

    this.handleChange = this.handleChange.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleChangeBlocked = this.handleChangeBlocked.bind(this);
  }

  componentDidMount() {
    this.setInitialState();
  }

  setInitialState() {
    const { term } = this.props;

    if (term && term.name) {
      this.setState({ name: term.name, blocked: term.blocked }, () => {
        this.inputRef.selectionStart = this.inputRef.value.length;
        this.inputRef.selectionEnd = this.inputRef.value.length;
      });
    }
  }

  handleUpdate() {
    const {
      intl,
      dispatch,
      term,
      taxonomyId,
      afterCreateTerm,
      handleClose,
    } = this.props;
    const {
      name,
      blocked,
    } = this.state;

    const errors = [];
    if (!name) {
      errors.push('name');
    }
    this.setState({ errors });
    if (errors.length > 0) {
      return;
    }

    this.setState({
      saving: true,
      errors: [],
    });

    if (term) {
      term.name = name;
      term.blocked = blocked;
      APIupdateTerm(term).then(() => {
        dispatch(fetchTaxonomies());
        afterCreateTerm(term);
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Your changes have been saved' }),
        });
        handleClose();
      }).catch((e) => {
        if (e.response.status === 409) {
          this.setState({ errors: ['exists'] });
        }
        this.setState({
          saving: false,
        });
      });
    } else {
      APIcreateTerm(taxonomyId, { name, blocked }).then((response) => {
        dispatch(fetchTaxonomies());
        afterCreateTerm(response);
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'New tag has been created' }),
        });
        handleClose();
      }).catch((e) => {
        if (e.response.status === 409) {
          this.setState({ errors: ['exists'] });
        }
        this.setState({
          saving: false,
        });
      });
    }
  }

  handleChange(e) {
    const { value } = e.target;
    this.setState({ name: value });
  }

  handleChangeBlocked(e) {
    const { checked } = e.target;
    this.setState({ blocked: checked });
  }

  render() {
    const {
      intl,
      term,
      handleClose,
      aiTagging,
    } = this.props;
    const {
      name,
      blocked,
      errors,
      saving,
    } = this.state;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {term ? intl.formatMessage({ defaultMessage: 'Edit tag' }) :
            intl.formatMessage({ defaultMessage: 'New tag' })}
        </ModalHeader>
        <ModalBody>
          <Form className="edit-term-form" onSubmit={(e) => { e.preventDefault(); this.handleUpdate(); }}>
            <FormGroup>
              <Label for="termName">
                {intl.formatMessage({ defaultMessage: 'Tag name' })}
              </Label>
              <Input
                type="text"
                id="termName"
                autoFocus={true}
                innerRef={(ref) => { this.inputRef = ref; }}
                disabled={saving || aiTagging}
                defaultValue={name}
                onChange={this.handleChange}
                invalid={errors.includes('name')}
              />
              {errors.includes('name') && (
                <FormFeedback>
                  {intl.formatMessage({ defaultMessage: 'This field is required' })}
                </FormFeedback>
              )}
              {errors.includes('exists') && (
                <FormFeedback style={{ display: 'block' }}>
                  {intl.formatMessage({ defaultMessage: 'Term with given name already exists' })}
                </FormFeedback>
              )}
              {aiTagging && (
                <small>
                  {intl.formatMessage({ defaultMessage: 'Tag name editing disabled for AI tagging category' })}
                </small>
              )}
            </FormGroup>
            <Label for="term-blocked-select" className="switch">
              <div className="switch-details">
                <div className="switch-control input-inner">
                  <Input
                    type="checkbox"
                    name="groupUploadFile"
                    id="term-blocked-select"
                    value="true"
                    checked={blocked}
                    onChange={this.handleChangeBlocked}
                  />
                  <i />
                </div>
                <span>
                  {intl.formatMessage({ defaultMessage: 'Blocked' })}
                </span>
              </div>
            </Label>
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving}
              onClick={this.handleUpdate}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Save' })}
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

TermEditModal.propTypes = {
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  aiTagging: PropTypes.bool.isRequired,
  term: PropTypes.object,
  taxonomyId: PropTypes.string,
  afterCreateTerm: PropTypes.func,
};

TermEditModal.defaultProps = {
  term: null,
  taxonomyId: null,
  afterCreateTerm: () => {},
};

export default connect()(injectIntl(TermEditModal));
